/**
 * Seaside theme — vanilla JS, no dependencies. Loaded with `defer`.
 * Handles: nav drawer, sticky header state, scroll-reveal, testimonial slider.
 */
(function () {
  'use strict';

  document.documentElement.classList.remove('no-js');

  /* ------------------------------------------------------------------
   * Navigation drawer
   * ------------------------------------------------------------------ */
  var drawerWrapper = document.getElementById('NavDrawer');
  var drawerToggles = document.querySelectorAll('[data-drawer-toggle]');

  function setDrawer(open) {
    if (!drawerWrapper) return;
    drawerWrapper.classList.toggle('is-open', open);
    drawerWrapper.setAttribute('aria-hidden', String(!open));

    // Compensate for the scrollbar that disappears with overflow:hidden,
    // otherwise the whole page shifts sideways when the drawer opens.
    if (open) {
      var scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.paddingRight = scrollbarWidth > 0 ? scrollbarWidth + 'px' : '';
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }

    drawerToggles.forEach(function (btn) {
      btn.setAttribute('aria-expanded', String(open));
    });

    if (open) {
      // Focus the panel itself (not the first link) so no focus ring flashes
      // on a moving element and the browser doesn't scroll mid-transition.
      var panel = drawerWrapper.querySelector('.drawer');
      if (panel) panel.focus({ preventScroll: true });
    }
  }

  drawerToggles.forEach(function (btn) {
    btn.addEventListener('click', function () {
      setDrawer(!drawerWrapper.classList.contains('is-open'));
    });
  });

  if (drawerWrapper) {
    drawerWrapper.addEventListener('click', function (event) {
      if (
        event.target.matches('[data-drawer-close]') ||
        event.target.closest('[data-drawer-close]')
      ) {
        setDrawer(false);
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && drawerWrapper.classList.contains('is-open')) {
        setDrawer(false);
      }
    });
  }

  /* ------------------------------------------------------------------
   * Sticky header state
   * ------------------------------------------------------------------ */
  var header = document.getElementById('SiteHeader');

  if (header) {
    var updateHeader = function () {
      header.classList.toggle('is-scrolled', window.scrollY > 50);
    };
    window.addEventListener('scroll', updateHeader, { passive: true });
    updateHeader();

    // El header es fixed, así que no ocupa espacio: el hero y las páginas
    // interiores necesitan saber su alto para no quedar debajo. Depende del
    // ancho del logo (ajustable) y de la proporción del archivo, por eso se
    // mide en vez de fijarse en CSS.
    var lastHeaderHeight = 0;

    var publishHeaderHeight = function () {
      // Al hacer scroll el header se comprime 8px. Si eso repropagara la
      // variable, el hero se recolocaría a media página (layout shift → CLS),
      // así que solo se mide en reposo.
      if (lastHeaderHeight && header.classList.contains('is-scrolled')) return;

      var height = header.offsetHeight;
      if (height === lastHeaderHeight) return;

      lastHeaderHeight = height;
      document.documentElement.style.setProperty('--header-h', height + 'px');
    };

    publishHeaderHeight();

    if ('ResizeObserver' in window) {
      // Cubre la carga del logo y el giro del dispositivo con una sola fuente
      // de verdad, sin adivinar cuándo termina de resolverse el layout.
      new ResizeObserver(publishHeaderHeight).observe(header);
    } else {
      window.addEventListener('resize', publishHeaderHeight);
      window.addEventListener('load', publishHeaderHeight);
    }
  }

  /* ------------------------------------------------------------------
   * Scroll-reveal
   * ------------------------------------------------------------------ */
  var revealTargets = document.querySelectorAll('.reveal');

  if ('IntersectionObserver' in window && revealTargets.length) {
    var revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
            revealObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    revealTargets.forEach(function (el) {
      revealObserver.observe(el);
    });
  } else {
    revealTargets.forEach(function (el) {
      el.classList.add('active');
    });
  }

  /* ------------------------------------------------------------------
   * Gallery lightbox (<dialog>: Esc + backdrop close come for free)
   * ------------------------------------------------------------------ */
  document.querySelectorAll('[data-gallery]').forEach(function (root) {
    var items = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-item]'));
    var dialog = root.querySelector('[data-gallery-lightbox]');
    if (!dialog || !items.length || typeof dialog.showModal !== 'function') return;

    var image = dialog.querySelector('[data-lightbox-image]');
    var caption = dialog.querySelector('[data-lightbox-caption]');
    var counter = dialog.querySelector('[data-lightbox-counter]');
    var current = 0;

    function show(next) {
      current = (next + items.length) % items.length;
      var data = items[current].dataset;
      image.src = data.full;
      image.alt = data.alt || '';
      caption.textContent = data.caption || '';
      counter.textContent = current + 1 + ' / ' + items.length;
    }

    items.forEach(function (item, itemIndex) {
      item.addEventListener('click', function () {
        show(itemIndex);
        dialog.showModal();
      });
    });

    dialog.querySelector('[data-lightbox-prev]').addEventListener('click', function () {
      show(current - 1);
    });

    dialog.querySelector('[data-lightbox-next]').addEventListener('click', function () {
      show(current + 1);
    });

    dialog.querySelector('[data-lightbox-close]').addEventListener('click', function () {
      dialog.close();
    });

    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) dialog.close();
    });

    dialog.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') show(current - 1);
      if (event.key === 'ArrowRight') show(current + 1);
    });

    dialog.addEventListener('close', function () {
      image.src = '';
    });
  });

  /* ------------------------------------------------------------------
   * Testimonial slider (auto-rotating, dot navigation)
   * ------------------------------------------------------------------ */
  document.querySelectorAll('[data-testimonials]').forEach(function (root) {
    var slides = root.querySelectorAll('[data-slide]');
    var dots = root.querySelectorAll('[data-dot]');
    if (slides.length < 2) return;

    var index = 0;
    var timer = null;
    var INTERVAL = parseInt(root.dataset.interval, 10) || 7000;

    function show(next) {
      slides[index].hidden = true;
      dots[index].setAttribute('aria-selected', 'false');
      index = (next + slides.length) % slides.length;
      slides[index].hidden = false;
      dots[index].setAttribute('aria-selected', 'true');
    }

    function startAuto() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      timer = window.setInterval(function () {
        show(index + 1);
      }, INTERVAL);
    }

    function stopAuto() {
      if (timer) window.clearInterval(timer);
    }

    dots.forEach(function (dot, dotIndex) {
      dot.addEventListener('click', function () {
        stopAuto();
        show(dotIndex);
        startAuto();
      });
    });

    startAuto();
  });
})();
